<div class="inbound-feeds-page">
  <div class="topline">
    <div>
      <h2>Inbound File Feeds</h2>
      <div class="subtitle">Customer-scoped feed definitions, import history, and row-level import errors.</div>
    </div>
    <div class="actions">
      <button mat-stroked-button type="button" (click)="scanUploadedFiles()" [disabled]="scanningUploadedFiles"
        matTooltip="Run the daily scan now to process new uploaded files.">
        <mat-icon>{{scanningUploadedFiles ? 'sync' : 'folder_open'}}</mat-icon>
        {{scanningUploadedFiles ? 'Scanning…' : 'Scan Uploaded Files'}}
      </button>
      <button mat-raised-button color="primary" type="button" (click)="openCreateFeed()">
        <mat-icon>add</mat-icon>
        New Feed
      </button>
    </div>
  </div>

  @if (capabilities) {
    <div class="inbound-addresses">
      <div class="address-item">
        <span class="label">Inbound email</span>
        <strong>{{capabilities.inboundAddresses.email || 'Not configured'}}</strong>
      </div>
      <div class="address-item">
        <span class="label">SFTP host</span>
        <strong>{{setupSftpHost() || 'Not configured'}}</strong>
      </div>
      <div class="address-item">
        <span class="label">Scope options</span>
        <strong>{{getScopeOptionCount()}}</strong>
      </div>
    </div>
  }

  @if (emailPollStatus || pollStatusLoaded) {
    <div class="poll-status-panel">
      @if (emailPollStatus) {
        <div class="poll-status-header">
          <div>
            <span class="label">Email Job Runner</span>
            <div class="poll-status-title">
              <strong>{{emailPollStatus.mailbox || capabilities?.inboundAddresses?.email || 'Not configured'}}</strong>
              @if (emailPollStatus?.lastRunStatus) {
                <span
                  class="status-pill"
                  [ngClass]="getPollStatusClass(emailPollStatus)"
                >{{getPollStatusLabel(emailPollStatus)}}</span>
              }
            </div>
            <div class="muted">{{getRunnerSummary(emailPollStatus) || 'Runner details unavailable.'}}</div>
          </div>
          <div class="poll-countdown">
            <div class="poll-status-actions">
              <button mat-stroked-button type="button" (click)="runPollNow()" [disabled]="!canRunPollNow()">
                <mat-icon>{{runningPollNow ? 'sync' : 'play_arrow'}}</mat-icon>
                {{runningPollNow ? 'Running…' : 'Run Now'}}
              </button>
            </div>
            <span class="label">Next poll</span>
            <strong>{{getPollCountdownLabel(emailPollStatus)}}</strong>
            <div class="muted">{{getPollTimestampLabel(emailPollStatus.nextPollAt)}}</div>
          </div>
        </div>
      } @else {
        <div class="empty compact-empty">
          {{loadingPollStatus ? 'Loading email poll status...' : 'Email poll status is not available.'}}
        </div>
      }
      @if (emailPollStatus) {
        <div class="poll-status-grid">
          <div>
            <span class="label">Last started</span>
            <strong>{{getPollTimestampLabel(emailPollStatus.lastStartedAt)}}</strong>
          </div>
          <div>
            <span class="label">Last finished</span>
            <strong>{{getPollTimestampLabel(emailPollStatus.lastFinishedAt)}}</strong>
          </div>
          <div>
            <span class="label">Last success</span>
            <strong>{{getPollTimestampLabel(emailPollStatus.lastSucceededAt)}}</strong>
          </div>
          <div>
            <span class="label">Last failure</span>
            <strong>{{getPollTimestampLabel(emailPollStatus.lastFailedAt)}}</strong>
          </div>
          <div>
            <span class="label">Last duration</span>
            <strong>{{getPollDurationLabel(emailPollStatus.lastDurationMs)}}</strong>
          </div>
          <div>
            <span class="label">Last refresh</span>
            <strong>{{getRelativeTimestampLabel(emailPollStatus.updatedAt)}}</strong>
          </div>
        </div>
      }
      @if (emailPollStatus?.lastErrorMessage) {
        <div class="poll-status-error">
          <span class="label">Last error</span>
          <div>{{emailPollStatus?.lastErrorMessage}}</div>
        </div>
      }
      @if (pollStatusRefreshFailed && emailPollStatus) {
        <div class="muted">
          Showing the last known runner state. A retry is scheduled automatically.
        </div>
      }
    </div>
  }

  <section class="filters-panel" aria-label="Filters and search">
    <div class="filters">
      <mat-form-field appearance="fill">
        <mat-label>Status</mat-label>
        <mat-select [(ngModel)]="filters.status" (selectionChange)="loadFeeds()">
          <mat-option value="all">All</mat-option>
          @for (status of capabilities?.statuses || []; track status) {
            <mat-option [value]="status">{{status}}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Scope type</mat-label>
        <mat-select [(ngModel)]="filters.scopeType" (selectionChange)="loadFeeds()">
          <mat-option value="all">All</mat-option>
          @for (scopeType of capabilities?.scopeTypes || []; track scopeType) {
            <mat-option [value]="scopeType">{{scopeType}}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Handler</mat-label>
        <mat-select [(ngModel)]="filters.handlerType" (selectionChange)="loadFeeds()">
          <mat-option value="all">All</mat-option>
          @for (handler of capabilities?.handlerTypes || []; track handler) {
            <mat-option [value]="handler">{{getHandlerLabel(handler)}}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Transport</mat-label>
        <mat-select [(ngModel)]="filters.transport" (selectionChange)="loadFeeds()">
          <mat-option value="all">All</mat-option>
          @for (transport of capabilities?.transports || []; track transport) {
            <mat-option [value]="transport">{{transport}}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="fill" class="search-field">
        <mat-label>Feed search</mat-label>
        <input matInput [(ngModel)]="filters.filter" (keyup.enter)="loadFeeds()" placeholder="Name, key, description, scope ID">
        <button mat-icon-button matSuffix type="button" aria-label="Search feeds" (click)="loadFeeds()">
          <mat-icon>search</mat-icon>
        </button>
      </mat-form-field>
    </div>

    <div class="scope-search">
      <mat-form-field appearance="fill">
        <mat-label>Scope type</mat-label>
        <mat-select [(ngModel)]="scopeOptionType" (selectionChange)="onScopeOptionTypeChange()">
          @for (scopeType of capabilities?.scopeTypes || []; track scopeType) {
            <mat-option [value]="scopeType">{{scopeType}}</mat-option>
          }
        </mat-select>
        <mat-hint>Scope options are loaded by type to keep the query bounded.</mat-hint>
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Scope option search</mat-label>
        <input matInput [(ngModel)]="scopeSearch" (ngModelChange)="onScopeSearchChange()" placeholder="PLX, company name, vendor product">
        <mat-hint>Optional. Results update automatically while you type.</mat-hint>
      </mat-form-field>
    </div>
  </section>

  <mat-tab-group [(selectedIndex)]="selectedTabIndex" (selectedTabChange)="$event.tab.textLabel === 'SFTP uploads' && loadSftpUploads()">
    <mat-tab label="Feeds">
      @if (sftpSetupFeed; as newFeed) {
        <div class="sftp-setup-prompt" role="status">
          <span>Feed saved. Set up SFTP access for {{newFeed.name}}.</span>
          <button mat-stroked-button type="button" (click)="openSftpAccess(newFeed)">Set up SFTP access</button>
        </div>
      }
      <div class="table-wrap">
        <table mat-table matSort #feedsSort="matSort" [dataSource]="feeds" class="table">
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
            <td mat-cell *matCellDef="let feed">
              <button mat-button type="button" class="link-button" (click)="$event.stopPropagation(); onFeedSelected(feed)">
                {{feed.name}}
              </button>
              <div class="muted mono">{{feed.feedKey}}</div>
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let feed">
              <span class="status-pill" [class.status-active]="feed.status === 'active'" [class.status-paused]="feed.status === 'paused'" [class.status-archived]="feed.status === 'archived'">{{feed.status}}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="scope">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Scope</th>
            <td mat-cell *matCellDef="let feed">{{getScopeLabel(feed)}}</td>
          </ng-container>

          <ng-container matColumnDef="handlerType">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Handler type</th>
            <td mat-cell *matCellDef="let feed">{{getHandlerLabel(feed.handlerType)}}</td>
          </ng-container>

          <ng-container matColumnDef="transports">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Transports</th>
            <td mat-cell *matCellDef="let feed">
              @if (feed.transportConfig?.sftp) {
                <div class="sftp-row-access">
                  <span>{{feed.transportConfig.sftp.processingMode === 'upload' ? 'On upload completion' : 'Daily scan'}}</span>
                  <span class="status-pill" [class.status-active]="sftpStatuses[feed.id] === 'ready'">{{sftpStatusLabel(feed)}}</span>
                </div>
              }
              <mat-chip-listbox aria-label="Configured transports">
                @for (transport of getTransportLabels(feed); track transport) {
                  <mat-chip-option disabled>{{transport}}</mat-chip-option>
                }
              </mat-chip-listbox>
            </td>
          </ng-container>

          <ng-container matColumnDef="lastImportStatus">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Last import status</th>
            <td mat-cell *matCellDef="let feed">{{feed.lastImportStatus || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="lastImportAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Last import time</th>
            <td mat-cell *matCellDef="let feed">{{feed.lastImportAt ? (feed.lastImportAt | date:'medium') : '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="updatedAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Updated time</th>
            <td mat-cell *matCellDef="let feed">{{feed.updatedAt ? (feed.updatedAt | date:'medium') : '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let feed">
              <button
                mat-icon-button
                [matMenuTriggerFor]="feedMenu"
                [matMenuTriggerData]="{feed: feed}"
                aria-label="Feed actions"
                (click)="$event.stopPropagation()"
                >
                <mat-icon>more_vert</mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="feedColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: feedColumns"
            class="feed-row"
            [class.selected-row]="isSelectedFeed(row)"
            (click)="onFeedSelected(row)"
          ></tr>
        </table>
        @if (loadingFeeds) {
          <div class="loading">Loading feeds...</div>
        }
        @if (!loadingFeeds && feeds.data.length === 0) {
          <div class="empty">No inbound feeds match the current filters.</div>
        }
      </div>
    </mat-tab>

    <mat-tab label="Unmatched Messages">
      <div class="tab-toolbar">
        <mat-form-field appearance="fill">
          <mat-label>Status</mat-label>
          <mat-select [(ngModel)]="unmatchedMessageFilters.status" (selectionChange)="loadUnmatchedMessages()">
            <mat-option value="open">Open</mat-option>
            <mat-option value="resolved">Resolved</mat-option>
            <mat-option value="ignored">Ignored</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="fill">
          <mat-label>Transport</mat-label>
          <mat-select [(ngModel)]="unmatchedMessageFilters.transport" (selectionChange)="loadUnmatchedMessages()">
            <mat-option value="all">All</mat-option>
            <mat-option value="email">Email</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="fill" class="search-field">
          <mat-label>Inbox search</mat-label>
          <input
            matInput
            [(ngModel)]="unmatchedMessageFilters.filter"
            (keyup.enter)="loadUnmatchedMessages()"
            placeholder="Sender, subject, file name, error"
            >
          <button mat-icon-button matSuffix type="button" aria-label="Search unmatched messages" (click)="loadUnmatchedMessages()">
            <mat-icon>search</mat-icon>
          </button>
        </mat-form-field>
      </div>

      <div class="table-wrap">
        <table mat-table matSort #unmatchedMessagesSort="matSort" [dataSource]="unmatchedMessages" class="table">
          <ng-container matColumnDef="createdAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Created</th>
            <td mat-cell *matCellDef="let message">{{getPollTimestampLabel(message.createdAt)}}</td>
          </ng-container>

          <ng-container matColumnDef="transport">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Transport</th>
            <td mat-cell *matCellDef="let message">{{message.transport || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="sourceIdentifier">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Source</th>
            <td mat-cell *matCellDef="let message">{{message.sourceIdentifier || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="subject">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Subject</th>
            <td mat-cell *matCellDef="let message">{{message.subject || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="originalFileName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Original file</th>
            <td mat-cell *matCellDef="let message">{{message.originalFileName || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="errorCode">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Error code</th>
            <td mat-cell *matCellDef="let message">{{message.errorCode || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="message">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Message</th>
            <td mat-cell *matCellDef="let message">{{message.message}}</td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let message">
              <span class="status-pill" [ngClass]="getUnmatchedStatusClass(message.status)">{{message.status}}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let message">
              <button
                mat-icon-button
                [matMenuTriggerFor]="unmatchedMessageMenu"
                [matMenuTriggerData]="{message: message}"
                aria-label="Unmatched message actions"
                (click)="$event.stopPropagation()"
                >
                <mat-icon>more_vert</mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="unmatchedMessageColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: unmatchedMessageColumns"
            [class.selected-row]="isSelectedUnmatchedMessage(row)"
            (click)="selectUnmatchedMessage(row)"
          ></tr>
        </table>
        @if (loadingUnmatchedMessages) {
          <div class="loading">Loading unmatched messages...</div>
        }
        @if (!loadingUnmatchedMessages && unmatchedMessages.data.length === 0) {
          <div class="empty">
            No unmatched messages match the current filters.
          </div>
        }
      </div>

      @if (selectedUnmatchedMessage) {
        <div class="detail">
          <div class="detail-header">
            <div>
              <h3>{{selectedUnmatchedMessage.subject || selectedUnmatchedMessage.sourceIdentifier || 'Unmatched message'}}</h3>
              <div class="muted">{{selectedUnmatchedMessage.message}}</div>
            </div>
            <div class="actions">
              <button mat-stroked-button type="button" (click)="openUnmatchedMessageStatusDialog(selectedUnmatchedMessage, 'resolved')">
                <mat-icon>check_circle</mat-icon>
                Resolve
              </button>
              <button mat-stroked-button type="button" (click)="openUnmatchedMessageStatusDialog(selectedUnmatchedMessage, 'ignored')">
                <mat-icon>block</mat-icon>
                Ignore
              </button>
              <button mat-stroked-button type="button" (click)="openUnmatchedMessageStatusDialog(selectedUnmatchedMessage, 'open')">
                <mat-icon>undo</mat-icon>
                Re-open
              </button>
            </div>
          </div>
          <div class="summary-grid">
            <div><span class="label">Created</span><strong>{{getPollTimestampLabel(selectedUnmatchedMessage.createdAt)}}</strong></div>
            <div><span class="label">Transport</span><strong>{{selectedUnmatchedMessage.transport || '-'}}</strong></div>
            <div><span class="label">Source identifier</span><strong>{{selectedUnmatchedMessage.sourceIdentifier || '-'}}</strong></div>
            <div><span class="label">Subject</span><strong>{{selectedUnmatchedMessage.subject || '-'}}</strong></div>
            <div><span class="label">Original file name</span><strong>{{selectedUnmatchedMessage.originalFileName || '-'}}</strong></div>
            <div><span class="label">Error code</span><strong>{{selectedUnmatchedMessage.errorCode || '-'}}</strong></div>
            <div><span class="label">Checksum</span><strong class="mono">{{selectedUnmatchedMessage.checksum || '-'}}</strong></div>
            <div>
              <span class="label">Status</span>
              <strong><span class="status-pill" [ngClass]="getUnmatchedStatusClass(selectedUnmatchedMessage.status)">{{selectedUnmatchedMessage.status}}</span></strong>
            </div>
          </div>
          <div class="config-grid">
            <section>
              <h4>Attachments</h4>
              @if (getUnmatchedAttachments(selectedUnmatchedMessage).length) {
                <div>
                  <div class="attachment-list">
                    @for (attachment of getUnmatchedAttachments(selectedUnmatchedMessage); track attachment) {
                      <div class="attachment-item">
                        <pre>{{formatJson(attachment)}}</pre>
                      </div>
                    }
                  </div>
                </div>
              } @else {
                <p class="muted">No attachments metadata was returned for this message.</p>
              }
            </section>
            <section>
              <h4>Resolution</h4>
              <p><span class="label">Resolution note</span>{{selectedUnmatchedMessage.resolutionNote || '-'}}</p>
              <p><span class="label">Resolved at</span>{{getPollTimestampLabel(selectedUnmatchedMessage.resolvedAt)}}</p>
              <p><span class="label">Resolved by user ID</span>{{selectedUnmatchedMessage.resolvedByUserId || '-'}}</p>
            </section>
          </div>
          @if (selectedUnmatchedMessage.bodyText) {
            <section class="format-panel">
              <h4>Body Text</h4>
              <pre>{{selectedUnmatchedMessage.bodyText}}</pre>
            </section>
          }
        </div>
      } @else {
        <div class="empty detail-empty">Select a message to review unmatched-email details and triage it.</div>
      }

    </mat-tab>

    <mat-tab label="Handler Formats">
      @if (handlerFormatItems.length) {
        <div class="formats-tab">
          <mat-accordion>
            @for (item of handlerFormatItems; track item) {
              <mat-expansion-panel [expanded]="selectedFeed?.handlerType === item.handlerType">
                <mat-expansion-panel-header>
                  <mat-panel-title>{{item.format.label || getHandlerLabel(item.handlerType)}}</mat-panel-title>
                  <mat-panel-description>
                    {{getRequiredScopeLabel(item.format)}} - {{getFileTypesLabel(item.format)}}
                  </mat-panel-description>
                </mat-expansion-panel-header>
                <div class="format-header">
                  <div class="muted">{{item.format.behavior || 'No processing notes provided.'}}</div>
                  <div class="format-meta">
                    <span><strong>Handler:</strong> <span class="mono">{{item.handlerType}}</span></span>
                    <span><strong>Header row:</strong> {{getHeaderRowLabel(item.format)}}</span>
                    <span><strong>Reject extra columns:</strong> {{getRejectUnknownColumnsLabel(item.format)}}</span>
                  </div>
                </div>
                @if (canDownloadTemplate(item)) {
                  <div class="format-actions">
                    @for (downloadType of item.downloadTypes; track downloadType) {
                      <button
                        mat-stroked-button
                        type="button"
                        (click)="downloadTemplate(item, downloadType)"
                        >
                        <mat-icon>download</mat-icon>
                        {{downloadType.toUpperCase()}} template
                      </button>
                    }
                  </div>
                }
                @if (item.format.fields?.length) {
                  <div>
                    @if (item.requiredFields.length) {
                      <div class="format-field-group">
                        <h5>Required Columns</h5>
                        <div class="format-table-wrap">
                          <table class="format-table">
                            <thead>
                              <tr>
                                <th>Column</th>
                                <th>Required</th>
                                <th>Type</th>
                                <th>Formats / values</th>
                                <th>Description</th>
                                <th>Maps to</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (field of item.requiredFields; track field) {
                                <tr>
                                  <td class="mono">{{field.name}}</td>
                                  <td>{{getFieldRequiredLabel(field)}}</td>
                                  <td>{{field.type || '-'}}</td>
                                  <td>
                                    <div>{{getFieldFormatsLabel(field)}}</div>
                                    @if (getFieldAcceptedValuesLabel(field) !== '-') {
                                      <div class="muted">{{getFieldAcceptedValuesLabel(field)}}</div>
                                    }
                                  </td>
                                  <td>{{field.description || '-'}}</td>
                                  <td class="mono">{{field.mapsTo || '-'}}</td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      </div>
                    }
                    @if (item.optionalFields.length) {
                      <div class="format-field-group">
                        <h5>Optional Columns</h5>
                        <div class="format-table-wrap">
                          <table class="format-table">
                            <thead>
                              <tr>
                                <th>Column</th>
                                <th>Required</th>
                                <th>Type</th>
                                <th>Formats / values</th>
                                <th>Description</th>
                                <th>Maps to</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (field of item.optionalFields; track field) {
                                <tr>
                                  <td class="mono">{{field.name}}</td>
                                  <td>{{getFieldRequiredLabel(field)}}</td>
                                  <td>{{field.type || '-'}}</td>
                                  <td>
                                    <div>{{getFieldFormatsLabel(field)}}</div>
                                    @if (getFieldAcceptedValuesLabel(field) !== '-') {
                                      <div class="muted">{{getFieldAcceptedValuesLabel(field)}}</div>
                                    }
                                  </td>
                                  <td>{{field.description || '-'}}</td>
                                  <td class="mono">{{field.mapsTo || '-'}}</td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      </div>
                    }
                  </div>
                } @else {
                  <div class="empty compact-empty">No business columns are required for this handler.</div>
                }
              </mat-expansion-panel>
            }
          </mat-accordion>
        </div>
      } @else {
        <div class="empty detail-empty">No handler format metadata was returned by the backend.</div>
      }
    </mat-tab>

    <mat-tab label="Imports">
      @if (selectedFeed) {
        <div class="tab-toolbar">
          <mat-form-field appearance="fill">
            <mat-label>Import status</mat-label>
            <mat-select [(ngModel)]="importFilters.status" (selectionChange)="loadImports()">
              <mat-option value="all">All</mat-option>
              @for (status of capabilities?.importStatuses || []; track status) {
                <mat-option [value]="status">{{status}}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      }
      <div class="table-wrap">
        <table mat-table matSort #importsSort="matSort" [dataSource]="imports" class="table">
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let item">{{item.status}}</td>
          </ng-container>
          <ng-container matColumnDef="transport">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Transport</th>
            <td mat-cell *matCellDef="let item">{{item.transport}}</td>
          </ng-container>
          <ng-container matColumnDef="originalFileName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>File</th>
            <td mat-cell *matCellDef="let item">{{item.originalFileName || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="sourceIdentifier">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Source</th>
            <td mat-cell *matCellDef="let item">{{item.sourceIdentifier || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="counts">
            <th mat-header-cell *matHeaderCellDef>Counts</th>
            <td mat-cell *matCellDef="let item">
              {{item.successCount || 0}} ok / {{item.errorCount || 0}} errors / {{item.warningCount || 0}} warnings
            </td>
          </ng-container>
          <ng-container matColumnDef="receivedAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Received</th>
            <td mat-cell *matCellDef="let item">{{item.receivedAt ? (item.receivedAt | date:'medium') : '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="finishedAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Finished</th>
            <td mat-cell *matCellDef="let item">{{item.finishedAt ? (item.finishedAt | date:'medium') : '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let item">
              <button
                mat-icon-button
                [matMenuTriggerFor]="importMenu"
                [matMenuTriggerData]="{item: item}"
                aria-label="Import actions"
                (click)="$event.stopPropagation()"
                >
                <mat-icon>more_vert</mat-icon>
              </button>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="importColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: importColumns" [class.selected-row]="selectedImport?.id === row.id" (click)="selectImport(row)"></tr>
        </table>
        @if (selectedFeed && imports.data.length === 0) {
          <div class="empty">No imports for this feed.</div>
        }
        @if (!selectedFeed) {
          <div class="empty">Select a feed to view imports.</div>
        }
      </div>
    </mat-tab>

    <mat-tab label="Errors">
      @if (selectedFeed) {
        <div class="tab-toolbar">
          <mat-form-field appearance="fill">
            <mat-label>Error status</mat-label>
            <mat-select [(ngModel)]="errorFilters.status" (selectionChange)="loadErrors($safeNavigationMigration(selectedImport?.id))">
              <mat-option value="all">All</mat-option>
              @for (status of capabilities?.errorStatuses || []; track status) {
                <mat-option [value]="status">{{status}}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="fill">
            <mat-label>Severity</mat-label>
            <mat-select [(ngModel)]="errorFilters.severity" (selectionChange)="loadErrors($safeNavigationMigration(selectedImport?.id))">
              <mat-option value="all">All</mat-option>
              @for (severity of capabilities?.errorSeverities || []; track severity) {
                <mat-option [value]="severity">{{severity}}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button mat-stroked-button type="button" (click)="selectedImport = null; loadErrors()">
            <mat-icon>filter_alt_off</mat-icon>
            All Feed Errors
          </button>
          @if (selectedImport) {
            <span class="muted">Filtered to import {{selectedImport.id}}</span>
          }
        </div>
      }
      <div class="table-wrap">
        <table mat-table matSort #errorsSort="matSort" [dataSource]="errors" class="table">
          <ng-container matColumnDef="rowNumber">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Row</th>
            <td mat-cell *matCellDef="let error">{{error.rowNumber || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="fieldName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Field</th>
            <td mat-cell *matCellDef="let error">{{error.fieldName || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="severity">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Severity</th>
            <td mat-cell *matCellDef="let error">{{error.severity}}</td>
          </ng-container>
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let error">{{error.status}}</td>
          </ng-container>
          <ng-container matColumnDef="message">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Message</th>
            <td mat-cell *matCellDef="let error">{{error.message}}</td>
          </ng-container>
          <ng-container matColumnDef="resolutionNote">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Resolution note</th>
            <td mat-cell *matCellDef="let error">{{error.resolutionNote || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let error">
              <button
                mat-icon-button
                [matMenuTriggerFor]="errorMenu"
                [matMenuTriggerData]="{error: error}"
                aria-label="Error actions"
                (click)="$event.stopPropagation()"
                >
                <mat-icon>more_vert</mat-icon>
              </button>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="errorColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: errorColumns"></tr>
        </table>
        @if (selectedFeed && errors.data.length === 0) {
          <div class="empty">No errors match the current filters.</div>
        }
        @if (!selectedFeed) {
          <div class="empty">Select a feed to view errors.</div>
        }
      </div>
    </mat-tab>
    <mat-tab label="SFTP uploads">
      <div class="sftp-upload-history">
        <div class="sftp-history-toolbar">
          <div>
            <h3>Upload history</h3>
            <p class="muted">Files received through SFTP. Files handled by scheduled or manual scans appear once discovered.</p>
          </div>
          <button mat-stroked-button type="button" (click)="loadSftpUploads()" [disabled]="loadingSftpUploads">
            <mat-icon aria-hidden="true">refresh</mat-icon> Refresh uploads
          </button>
        </div>
        @if (sftpUploadsError) {
          <p role="alert">{{sftpUploadsError}}</p>
        }
        @if (!loadingSftpUploads && !sftpUploadsError && !sftpUploads.length) {
          <p>No SFTP files recorded.</p>
        }
        @for (upload of sftpUploads; track upload) {
          <article class="sftp-upload-entry">
            <header class="sftp-upload-header">
              <div class="sftp-file-heading">
                <mat-icon class="sftp-file-icon" aria-hidden="true">description</mat-icon>
                <div>
                  <h4>{{upload.filePath?.split('/')?.pop()}}</h4>
                  @if (upload.connectionName) {
                    <p class="muted">{{upload.connectionName}}</p>
                  }
                </div>
              </div>
              <span class="sftp-status-pill" [attr.data-status]="upload.status">{{upload.status | titlecase}}</span>
            </header>
            <div class="sftp-upload-meta">
              <span class="sftp-mode-pill">{{upload.processingMode === 'upload' ? 'On upload completion' : 'Scheduled / manual scan'}}</span>
              <span>{{upload.attempts}} {{upload.attempts === 1 ? 'attempt' : 'attempts'}}</span>
              <span>{{upload.recoveryCount}} {{upload.recoveryCount === 1 ? 'recovery' : 'recoveries'}}</span>
            </div>
            <dl class="sftp-upload-timeline">
              <div><dt>Uploaded</dt><dd>{{upload.uploadedAt ? (upload.uploadedAt | date:'medium') : 'Not recorded'}}</dd></div>
              <div><dt>First seen</dt><dd>{{upload.firstSeenAt | date:'medium'}}</dd></div>
              @if (upload.queuedAt) {
                <div><dt>Queued</dt><dd>{{upload.queuedAt | date:'medium'}}</dd></div>
              }
              <div><dt>Processing started</dt><dd>{{upload.startedAt ? (upload.startedAt | date:'medium') : '—'}}</dd></div>
              <div><dt>Finished</dt><dd>{{upload.finishedAt ? (upload.finishedAt | date:'medium') : '—'}}</dd></div>
              @if (upload.nextAttemptAt) {
                <div><dt>Next attempt</dt><dd>{{upload.nextAttemptAt | date:'medium'}}</dd></div>
              }
            </dl>
            @if (upload.errorCode) {
              <div class="sftp-upload-error" role="status">
                <mat-icon aria-hidden="true">error_outline</mat-icon>
                <span>{{upload.errorCode}}</span>
              </div>
            }
            @if (upload.importAttempts.length || upload.canRetry) {
              <footer class="sftp-upload-footer">
                @if (upload.importAttempts.length) {
                  <div class="sftp-import-attempts">
                    <h5>Import attempts</h5>
                    <div class="sftp-attempt-list">
                      @for (attempt of upload.importAttempts; track attempt; let index = $index) {
                        <button mat-stroked-button type="button" class="sftp-attempt-button"
                          (click)="openSftpImport(attempt.id)">
                          <span class="sftp-attempt-content">
                            <span class="sftp-attempt-heading">Attempt {{index + 1}}<mat-icon aria-hidden="true">arrow_forward</mat-icon></span>
                            <span class="sftp-attempt-date">{{attempt.receivedAt | date:'medium'}}</span>
                            <span class="sftp-status-pill" [attr.data-status]="attempt.status">{{attempt.status | titlecase}}</span>
                          </span>
                        </button>
                      }
                    </div>
                  </div>
                }
                @if (upload.canRetry) {
                  <button mat-stroked-button type="button" class="sftp-retry-button"
                  [disabled]="!!retryingUpload" (click)="retrySftpUpload(upload)">Retry upload</button>
                }
              </footer>
            }
          </article>
        }
      </div>
    </mat-tab>
  </mat-tab-group>

  <mat-menu #feedMenu="matMenu">
    <ng-template matMenuContent let-feed="feed">
      @if (feed.transportConfig?.sftp) {
        <button mat-menu-item (click)="browseFiles(feed)">
          <mat-icon>folder_open</mat-icon>
          <span>Browse files</span>
        </button>
      }
      @if (feed.transportConfig?.sftp) {
        <button mat-menu-item (click)="openSftpAccess(feed)">
          <mat-icon>settings</mat-icon>
          <span>Manage SFTP</span>
        </button>
      }
      <button mat-menu-item (click)="showFeedImports(feed)">
        <mat-icon>list_alt</mat-icon>
        <span>Show Imports</span>
      </button>
      <button mat-menu-item (click)="showFeedErrors(feed)">
        <mat-icon>report_problem</mat-icon>
        <span>Show Errors</span>
      </button>
      <button mat-menu-item (click)="openEditFeed(feed)">
        <mat-icon>edit</mat-icon>
        <span>Edit</span>
      </button>
      @if (feed.status === 'archived') {
        <button mat-menu-item (click)="restoreFeed(feed, 'active')">
          <mat-icon>unarchive</mat-icon>
          <span>Restore active</span>
        </button>
      }
      @if (feed.status !== 'archived') {
        <button mat-menu-item (click)="archiveFeed(feed)">
          <mat-icon>archive</mat-icon>
          <span>Archive</span>
        </button>
      }
    </ng-template>
  </mat-menu>

  <mat-menu #importMenu="matMenu">
    <ng-template matMenuContent let-item="item">
      <button mat-menu-item (click)="openImportDetails(item)">
        <mat-icon>visibility</mat-icon>
        <span>View details</span>
      </button>
      <button mat-menu-item (click)="showImportErrors(item)">
        <mat-icon>report_problem</mat-icon>
        <span>View errors</span>
      </button>
    </ng-template>
  </mat-menu>

  <mat-menu #errorMenu="matMenu">
    <ng-template matMenuContent let-error="error">
      <button mat-menu-item (click)="openErrorDetails(error)">
        <mat-icon>visibility</mat-icon>
        <span>View details</span>
      </button>
      <button mat-menu-item (click)="openErrorStatusDialog(error, 'resolved')">
        <mat-icon>check_circle</mat-icon>
        <span>Resolve</span>
      </button>
      <button mat-menu-item (click)="openErrorStatusDialog(error, 'ignored')">
        <mat-icon>block</mat-icon>
        <span>Ignore</span>
      </button>
      <button mat-menu-item (click)="openErrorStatusDialog(error, 'open')">
        <mat-icon>undo</mat-icon>
        <span>Reopen</span>
      </button>
    </ng-template>
  </mat-menu>

  <mat-menu #unmatchedMessageMenu="matMenu">
    <ng-template matMenuContent let-message="message">
      <button mat-menu-item (click)="selectUnmatchedMessage(message)">
        <mat-icon>visibility</mat-icon>
        <span>View details</span>
      </button>
      <button mat-menu-item (click)="openUnmatchedMessageStatusDialog(message, 'resolved')">
        <mat-icon>check_circle</mat-icon>
        <span>Resolve</span>
      </button>
      <button mat-menu-item (click)="openUnmatchedMessageStatusDialog(message, 'ignored')">
        <mat-icon>block</mat-icon>
        <span>Ignore</span>
      </button>
      <button mat-menu-item (click)="openUnmatchedMessageStatusDialog(message, 'open')">
        <mat-icon>undo</mat-icon>
        <span>Re-open</span>
      </button>
    </ng-template>
  </mat-menu>
</div>

<ng-template #sftpPanel>
  <div class="sftp-panel-content">
    <div class="sftp-panel-header">
      <h2>SFTP access</h2>
      <button mat-icon-button type="button" aria-label="Close SFTP access" [disabled]="sftpBusy" (click)="closeSftpAccess()"><mat-icon>close</mat-icon></button>
    </div>
    <app-inbound-sftp-access [feed]="sftpFeed" (busyChanged)="onSftpBusy($event)" (statusChanged)="onSftpStatus($event)" (feedUpdated)="onSftpFeedUpdated($event)"></app-inbound-sftp-access>
  </div>
</ng-template>

<ng-template #filesPanel>
  <div class="sftp-panel-content files-panel" cdkDrag cdkDragRootElement=".cdk-overlay-pane" cdkDragBoundary=".cdk-overlay-container">
    <div class="sftp-panel-header">
      <h2 cdkDragHandle title="Drag to move">Browse files — {{filesFeed?.name}}</h2>
      <button mat-icon-button type="button" [disabled]="filesBusy" aria-label="Close file browser" (click)="closeFiles()"><mat-icon>close</mat-icon></button>
    </div>
    <p class="files-folder">Directory: {{filePage?.folder || filesFeed?.transportConfig?.sftp?.folder}}</p>
    <button mat-stroked-button type="button" [disabled]="filesLoading || filesBusy" (click)="loadFiles()">Refresh</button>
    <input #fileUpload type="file" hidden (change)="addFile($event)">
    <button mat-flat-button color="primary" type="button" class="add-file-button" title="Upload a file up to 25 MB to this directory" [disabled]="filesLoading || filesBusy" (click)="fileUpload.click()">Add File</button>
    @if (filesBusy) {
      <p role="status">Working…</p>
    }
    @if (filesLoading) {
      <p role="status">Loading files…</p>
    }
    @if (filesError) {
      <p role="alert">{{filesError}}</p>
    }
    @if (filePage; as page) {
      @if (!filesLoading && !filesError && !page.files.length && !page.nextToken) {
        <p>No files in this directory.</p>
      }
      @if (page.files.length) {
        <div class="files-table-wrap">
          <table class="files-table" matSort [matSortActive]="filesSort.active" [matSortDirection]="filesSort.direction"
            [matSortDisableClear]="true" (matSortChange)="sortFiles($event)">
            <thead><tr>
              <th scope="col" mat-sort-header="name" sortActionDescription="Sort by filename">Filename</th>
              <th scope="col" mat-sort-header="size" sortActionDescription="Sort by size">Size (bytes)</th>
              <th scope="col" mat-sort-header="lastModified" sortActionDescription="Sort by last modified">Last modified</th>
              <th scope="col">Processing status</th>
              <th scope="col" class="file-row-actions" aria-label="File actions"></th>
            </tr></thead>
            <tbody>@for (file of page.files; track file) {
              <tr>
                <td>{{file.name}}</td>
                <td>{{file.size | number}}</td>
                <td>{{file.lastModified | date:'medium'}}</td>
                <td><span class="status-pill" [class.status-active]="file.processingStatus === 'processed'"
                [class.file-status-error]="file.processingStatus === 'errors'">{{fileProcessingStatusLabel(file.processingStatus)}}</span></td>
                <td class="file-row-actions">
                  <button mat-icon-button type="button" [matMenuTriggerFor]="fileMenu" [disabled]="filesBusy || filesLoading" [attr.aria-label]="'File actions for ' + file.name"><mat-icon>more_vert</mat-icon></button>
                  <mat-menu #fileMenu="matMenu">
                    <button mat-menu-item (click)="openFile(file, 'view')"><mat-icon>visibility</mat-icon><span>View</span></button>
                    <button mat-menu-item (click)="openFile(file, 'download')"><mat-icon>download</mat-icon><span>Download</span></button>
                    <button mat-menu-item (click)="beginFileAction('move', file)"><mat-icon>drive_file_move</mat-icon><span>Move</span></button>
                    <button mat-menu-item (click)="beginFileAction('rename', file)"><mat-icon>edit</mat-icon><span>Rename</span></button>
                    <button mat-menu-item (click)="deleteFile(file)"><mat-icon>delete</mat-icon><span>Delete</span></button>
                  </mat-menu>
                </td>
              </tr>
            }</tbody>
          </table>
        </div>
      }
      @if (page.nextToken) {
        <p>Sorting applies to loaded files. Load more to include additional files.</p>
      }
      @if (page.nextToken) {
        <button mat-stroked-button type="button" [disabled]="filesLoading || filesBusy" (click)="loadFiles(true)">Load more</button>
      }
    }
  </div>
</ng-template>

<ng-template #deleteFilePanel let-file>
  <h2 mat-dialog-title>Delete file?</h2>
  <mat-dialog-content>
    <p>Delete <strong class="delete-file-name">{{file.name}}</strong> from the upload directory? This cannot be undone.</p>
  </mat-dialog-content>
  <mat-dialog-actions align="end" class="delete-file-actions">
    <button mat-button type="button" [mat-dialog-close]="false" cdkFocusInitial>Cancel</button>
    <button mat-flat-button color="warn" type="button" [mat-dialog-close]="true">Delete file</button>
  </mat-dialog-actions>
</ng-template>

<ng-template #fileActionPanel>
  <form class="file-action-form" (ngSubmit)="submitFileAction()">
    <h2 mat-dialog-title>{{fileAction === 'rename' ? 'Rename file' : 'Move file'}}</h2>
    <mat-dialog-content>
      <p class="file-action-name">{{actionFile?.name}}</p>
      <mat-form-field appearance="outline">
        <mat-label>{{fileAction === 'rename' ? 'New filename' : 'Destination folder'}}</mat-label>
        <input matInput name="fileDestination" [(ngModel)]="fileDestination" [disabled]="filesBusy" [required]="fileAction === 'rename'" cdkFocusInitial>
        @if (fileAction === 'move') {
          <mat-hint>Relative to this feed’s directory. Leave empty for the root directory.</mat-hint>
        }
      </mat-form-field>
      @if (filesBusy) {
        <p role="status">{{fileAction === 'rename' ? 'Renaming file…' : 'Moving file…'}}</p>
      }
      @if (filesError) {
        <p role="alert">{{filesError}}</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="file-action-buttons">
      <button mat-button type="button" [disabled]="filesBusy" (click)="cancelFileAction()">Cancel</button>
      <button mat-flat-button color="primary" type="submit" [disabled]="filesBusy || filesLoading">{{fileAction === 'rename' ? 'Rename file' : 'Move file'}}</button>
    </mat-dialog-actions>
  </form>
</ng-template>
